// Process [@mention](mention://user/1/Pranav) and internal chat entity mentions.
const MENTION_REGEX =
  /^mention:\/\/(user|team|contact|kanban_item|conversation)\/(\d+)\/([^?]+)(?:\?context=([^&]+))?$/;

const escapeHtml = value =>
  String(value ?? '').replace(/[&<>"']/g, character => {
    const entities = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    };
    return entities[character];
  });

const buildMentionTokens = () => (state, silent) => {
  var label;
  var labelEnd;
  var labelStart;
  var pos;
  var res;
  var token;
  var href = '';
  var max = state.posMax;

  if (state.src.charCodeAt(state.pos) !== 0x5b /* [ */) {
    return false;
  }

  labelStart = state.pos + 1;
  labelEnd = state.md.helpers.parseLinkLabel(state, state.pos, true);

  // parser failed to find ']', so it's not a valid link
  if (labelEnd < 0) {
    return false;
  }

  label = state.src.slice(labelStart, labelEnd);
  pos = labelEnd + 1;

  if (pos < max && state.src.charCodeAt(pos) === 0x28 /* ( */) {
    pos += 1;
    res = state.md.helpers.parseLinkDestination(state.src, pos, state.posMax);
    if (res.ok) {
      href = state.md.normalizeLink(res.str);
      if (state.md.validateLink(href)) {
        pos = res.pos;
      } else {
        href = '';
      }
    }
    pos += 1;
  }

  const mention = href.match(MENTION_REGEX);
  if (!mention) {
    return false;
  }

  let mentionContext = {};
  if (mention[4]) {
    try {
      mentionContext = JSON.parse(decodeURIComponent(mention[4]));
    } catch {
      mentionContext = {};
    }
  }

  if (!silent) {
    state.pos = labelStart;
    state.posMax = labelEnd;

    token = state.push('mention', '');
    token.href = href;
    token.content = label;
    token.mentionType = mention[1];
    token.mentionId = mention[2];
    token.mentionContext = mentionContext;
  }

  state.pos = pos;
  state.posMax = max;

  return true;
};

const renderMentions = () => (tokens, idx) => {
  const { content, mentionType, mentionId, mentionContext } = tokens[idx];
  const isEntityMention = ['contact', 'kanban_item', 'conversation'].includes(
    mentionType
  );
  if (isEntityMention) {
    const iconByType = {
      contact: 'i-lucide-contact',
      kanban_item: 'i-lucide-kanban',
      conversation: 'i-lucide-messages-square',
    };

    const category = mentionContext.category || mentionType.replace('_', ' ');
    const title = mentionContext.title || content.replace(/^@/, '');
    const details = [
      mentionContext.subtitle,
      mentionContext.detail,
      mentionContext.description,
    ]
      .filter(Boolean)
      .map(value => `<span class="block truncate">${escapeHtml(value)}</span>`)
      .join('');

    const popup = `<span class="invisible absolute bottom-full start-1/2 z-50 mb-2 w-72 max-w-[calc(100vw-2rem)] -translate-x-1/2 translate-y-1 rounded-xl border border-n-weak bg-n-solid-1 p-3 text-left text-n-slate-12 opacity-0 shadow-lg transition-all group-hover/mention:visible group-hover/mention:translate-y-0 group-hover/mention:opacity-100 group-focus-within/mention:visible group-focus-within/mention:translate-y-0 group-focus-within/mention:opacity-100" role="tooltip"><span class="mb-2 flex items-start gap-2.5"><span class="inline-flex size-8 flex-shrink-0 items-center justify-center rounded-lg bg-n-brand/10 text-n-brand"><i class="${iconByType[mentionType]} size-4" aria-hidden="true"></i></span><span class="flex min-w-0 flex-col"><span class="mb-0.5 block text-[10px] font-medium uppercase tracking-wide text-n-brand">${escapeHtml(category)}</span><span class="block truncate text-sm font-semibold">${escapeHtml(title)}</span></span></span>${details ? `<span class="flex flex-col gap-0.5 text-xs leading-4 text-n-slate-10">${details}</span>` : ''}</span>`;

    return `<span class="prosemirror-mention-node internal-chat-entity-mention group/mention relative inline-block cursor-pointer align-baseline text-inherit underline decoration-current/50 underline-offset-2 hover:decoration-current focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand" role="link" tabindex="0" data-mention-type="${mentionType}" data-mention-id="${mentionId}" aria-label="${escapeHtml(`${category}: ${title}`)}">${escapeHtml(content)}${popup}</span>`;
  }

  return `<span class="prosemirror-mention-node" data-mention-type="${mentionType}" data-mention-id="${mentionId}">${escapeHtml(content)}</span>`;
};

export default function mentionPlugin(md) {
  md.renderer.rules.mention = renderMentions(md);
  md.inline.ruler.before('link', 'mention', buildMentionTokens(md));
}
